import { useForm } from "@tanstack/react-form";
import { useIsFetching, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useId, useState } from "react";

import {
	API_KEY_LIMIT,
	apiKeyHint,
	apiPaths,
	createApiKeyRequestSchema,
	type ApiKey,
	type CreatedApiKey,
} from "@ravnar/api-contract";

import { apiKeysQueryOptions, createApiKey, revokeApiKey } from "../../api/api-keys";
import { appConfigQueryOptions, integrationUrl } from "../../api/app-config";
import { useAskAgain } from "../../api/refresh";
import { PageHeader } from "../../components/shell/app-shell";
import { Button } from "../../components/ui/button";
import { Card, CardHeader } from "../../components/ui/card";
import { ConfirmDialog } from "../../components/ui/confirm-dialog";
import { Drawer } from "../../components/ui/drawer";
import { Empty } from "../../components/ui/empty";
import { Field, Input } from "../../components/ui/field";
import { Table, type TableColumn } from "../../components/ui/table";
import { Updating } from "../../components/ui/updating";
import { fieldErrorMessage, submitOnce } from "../../lib/form";
import { formatShortDate } from "../../lib/format";
import { showToast } from "../../lib/toast";

export const Route = createFileRoute("/settings/api-keys")({
	// Prefetch through the Query cache so the page can use useSuspenseQuery without a loading flash.
	loader: ({ context }) =>
		Promise.all([
			context.queryClient.query({ ...apiKeysQueryOptions(), staleTime: "static" }),
			context.queryClient.query(appConfigQueryOptions()),
		]),
	head: () => ({ meta: [{ title: "API keys · Ravnar" }] }),
	staticData: { crumbs: ["Settings", "API keys"] },
	component: ApiKeysPage,
});

/**
 * The organization's API keys, for owners and admins: each one's name and last four characters, who created it and
 * when it was last used; creating one, which shows the key itself once, in the drawer that created it; and revoking one
 * behind a `ConfirmDialog`. The API holds an organization to `API_KEY_LIMIT` keys, and so does Create key.
 */
function ApiKeysPage() {
	const keysQuery = apiKeysQueryOptions();
	const { data } = useSuspenseQuery(keysQuery);
	const isUpdating = useIsFetching({ queryKey: keysQuery.queryKey, exact: true }) > 0;
	const [creating, setCreating] = useState(false);
	const [revoking, setRevoking] = useState<ApiKey | undefined>();
	const full = data.keys.length >= API_KEY_LIMIT;

	const columns: readonly TableColumn<ApiKey>[] = [
		{
			key: "key",
			header: "Key",
			cell: key => (
				<span className="block">
					<span className="block font-medium text-ink">{key.name}</span>
					<span className="block text-caption text-ink-3">{apiKeyHint(key)}</span>
				</span>
			),
		},
		{
			key: "created",
			header: "Created",
			cell: key => (
				<span className="block">
					<span className="block text-ink">{formatShortDate(key.createdAt.slice(0, 10))}</span>
					<span className="block text-caption text-ink-3">by {key.createdBy}</span>
				</span>
			),
		},
		{
			key: "used",
			header: "Last used",
			muted: true,
			cell: key => (key.lastUsedAt === undefined ? "Never" : formatShortDate(key.lastUsedAt.slice(0, 10))),
		},
		{
			key: "revoke",
			header: "Revoke",
			align: "right",
			cell: key => (
				<Button
					size="sm"
					aria-label={`Revoke ${key.name}`}
					onClick={() => {
						setRevoking(key);
					}}
				>
					Revoke
				</Button>
			),
		},
	];

	return (
		<>
			<PageHeader
				title="API keys"
				description="Keys your organization gives other software and chatbots, so that they can add people and parties to Ravnar, search its Directory and upload documents to Intake."
			/>
			<div className="flex flex-col gap-3.5">
				<Card>
					<CardHeader title="Keys" action={<Updating active={isUpdating} />} />
					{data.keys.length === 0 ? (
						<Empty>No API keys yet.</Empty>
					) : (
						<Table label="API keys" columns={columns} rows={data.keys} rowKey={key => key.id} />
					)}
					<div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-3">
						<Button
							variant="primary"
							disabled={full}
							onClick={() => {
								setCreating(true);
							}}
						>
							Create key
						</Button>
						<p className="text-caption text-ink-2">
							{data.keys.length} of {API_KEY_LIMIT} keys
							{full ? ". Revoke one to create another." : ""}
						</p>
					</div>
				</Card>
				<UsingAKey />
			</div>
			<CreateKeyDrawer
				open={creating}
				onClose={() => {
					setCreating(false);
				}}
			/>
			<RevokeKeyDialog
				apiKey={revoking}
				onClose={() => {
					setRevoking(undefined);
				}}
			/>
		</>
	);
}

/**
 * Where software sends a key, and how: the integration API's routes, and the MCP server a chatbot connects to, all at
 * the API's own address where the stage has one, and where the app reaches the API otherwise.
 */
function UsingAKey() {
	const { data: appConfig } = useSuspenseQuery(appConfigQueryOptions());
	const parties = integrationUrl(apiPaths.integrationParties, appConfig);
	const documents = integrationUrl(apiPaths.integrationDocuments, appConfig);
	const mcp = integrationUrl(apiPaths.integrationMcp, appConfig);
	return (
		<Card>
			<CardHeader title="Using a key" />
			<dl className="m-0 grid grid-cols-1 gap-x-4 gap-y-2 p-4 text-body sm:grid-cols-[160px_minmax(0,1fr)]">
				<dt className="text-ink-2">Send a party to</dt>
				<dd className="m-0 break-all text-ink">POST {parties}</dd>
				<dt className="text-ink-2">Upload a document to</dt>
				<dd className="m-0 break-all text-ink">POST {documents}</dd>
				<dt className="text-ink-2">Connect a chatbot to</dt>
				<dd className="m-0 break-all text-ink">{mcp}</dd>
				<dt className="text-ink-2">With the key as</dt>
				<dd className="m-0 break-all text-ink">Authorization: Bearer &lt;key&gt;</dd>
				<dt className="text-ink-2">It adds</dt>
				<dd className="m-0 text-ink">
					One person, vendor, customer or other party per request: its kind, and the fields its form in Ravnar
					asks for. For a document, send its file name and size, then PUT its bytes where the answer says;
					Intake reads it as it reads any upload. A chatbot's tools do both the same way, and search the
					Directory.
				</dd>
			</dl>
		</Card>
	);
}

interface CreateKeyDrawerProps {
	readonly open: boolean;
	/** Called on Cancel, Done, the close button and Escape. */
	readonly onClose: () => void;
}

/**
 * Creates a key: a name, then the key itself, shown once. The key is held by this drawer alone — its state, and the
 * create request's answer, which the query client drops as soon as the drawer lets go of it — so it is gone for good
 * once the drawer closes; the API keeps only its hash.
 */
function CreateKeyDrawer({ open, onClose }: CreateKeyDrawerProps) {
	const formId = useId();
	const queryClient = useQueryClient();
	const askAgain = useAskAgain();
	const [created, setCreated] = useState<CreatedApiKey | undefined>();
	// The keys live under "settings", and the audit entry arrives through a queue a moment after the answer.
	const mutation = useMutation({
		mutationFn: createApiKey,
		// The answer holds the key itself: the mutation cache drops it once the drawer resets the mutation, rather than
		// keeping it for the default five minutes.
		gcTime: 0,
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({ queryKey: ["settings"] }),
				askAgain(["settings", "audit-log"]),
			]),
	});

	const close = () => {
		const name = created?.key.name;
		setCreated(undefined);
		mutation.reset();
		onClose();
		if (name !== undefined) {
			showToast(`${name} created.`);
		}
	};

	return (
		<Drawer
			open={open}
			title={created === undefined ? "Create API key" : "Copy your new key"}
			onClose={close}
			footer={
				created === undefined ? (
					<>
						<Button onClick={close}>Cancel</Button>
						<Button type="submit" form={formId} variant="primary" disabled={mutation.isPending}>
							{mutation.isPending ? "Creating…" : "Create key"}
						</Button>
					</>
				) : (
					<Button variant="primary" onClick={close}>
						Done
					</Button>
				)
			}
		>
			{open ? (
				created === undefined ? (
					<div className="flex flex-col gap-4">
						<CreateKeyForm
							formId={formId}
							onCreate={async name => {
								try {
									setCreated(await mutation.mutateAsync(name));
								} catch {
									// The alert below shows mutation.error.
								}
							}}
						/>
						{mutation.error === null ? null : (
							<p role="alert" className="text-caption text-alert">
								{mutation.error.message}
							</p>
						)}
					</div>
				) : (
					<NewKey created={created} />
				)
			) : null}
		</Drawer>
	);
}

interface CreateKeyFormProps {
	/** Lets the drawer footer's submit button, which sits outside the <form>, submit it. */
	readonly formId: string;
	readonly onCreate: (name: string) => Promise<void>;
}

function CreateKeyForm({ formId, onCreate }: CreateKeyFormProps) {
	const form = useForm({
		defaultValues: { name: "" },
		// Zod schemas are Standard Schema validators, so no adapter is needed.
		validators: { onChange: createApiKeyRequestSchema, onSubmit: createApiKeyRequestSchema },
		onSubmit: async ({ value }) => {
			await onCreate(createApiKeyRequestSchema.parse(value).name);
		},
	});
	return (
		<form id={formId} noValidate className="flex flex-col gap-2.5" onSubmit={submitOnce(form)}>
			<form.Field name="name">
				{field => (
					<Field label="Name" error={fieldErrorMessage(field.state.meta)}>
						<Input
							name={field.name}
							placeholder="HubSpot sync"
							value={field.state.value}
							onBlur={field.handleBlur}
							onChange={event => {
								field.handleChange(event.target.value);
							}}
						/>
					</Field>
				)}
			</form.Field>
			<p className="text-caption text-ink-2">What the key is for: the software that will use it.</p>
		</form>
	);
}

/** The key itself, the one time it is ever shown, with a button that copies it. */
function NewKey({ created }: { readonly created: CreatedApiKey }) {
	const [copy, setCopy] = useState<"copied" | "failed" | undefined>();
	const copyKey = async () => {
		try {
			await navigator.clipboard.writeText(created.secret);
			setCopy("copied");
		} catch {
			setCopy("failed");
		}
	};
	return (
		<div className="flex flex-col gap-3">
			<p className="text-body text-ink-2">
				This is the key for {created.key.name}. Copy it now and give it to the software that will use it: Ravnar
				shows it only this once and keeps no copy, so a key that is lost can only be revoked and replaced.
			</p>
			<Field label="Key">
				<Input
					readOnly
					value={created.secret}
					onFocus={event => {
						event.currentTarget.select();
					}}
				/>
			</Field>
			<div className="flex flex-wrap items-center gap-2.5">
				<Button
					onClick={() => {
						void copyKey();
					}}
				>
					Copy key
				</Button>
				{copy === "copied" ? (
					<p role="status" className="text-caption text-ok">
						Copied.
					</p>
				) : null}
				{copy === "failed" ? (
					<p role="alert" className="text-caption text-alert">
						The browser did not let Ravnar copy it: select the key and copy it yourself.
					</p>
				) : null}
			</div>
		</div>
	);
}

interface RevokeKeyDialogProps {
	/** The key being revoked; undefined while the dialog is closed. */
	readonly apiKey: ApiKey | undefined;
	readonly onClose: () => void;
}

function RevokeKeyDialog({ apiKey, onClose }: RevokeKeyDialogProps) {
	const queryClient = useQueryClient();
	const askAgain = useAskAgain();
	const mutation = useMutation({
		mutationFn: revokeApiKey,
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({ queryKey: ["settings"] }),
				askAgain(["settings", "audit-log"]),
			]),
		// A key somebody else revoked a moment ago answers 404: the list is asked again, so it shows it gone.
		onError: () => queryClient.invalidateQueries({ queryKey: ["settings", "api-keys"] }),
	});
	const close = () => {
		mutation.reset();
		onClose();
	};
	const revoke = async (key: ApiKey) => {
		try {
			await mutation.mutateAsync(key.id);
		} catch {
			// The dialog shows the error.
			return;
		}
		close();
		showToast(`${key.name} revoked.`);
	};
	return (
		<ConfirmDialog
			open={apiKey !== undefined}
			title={`Revoke ${apiKey?.name ?? "the key"}?`}
			confirmLabel="Revoke key"
			busy={mutation.isPending}
			error={mutation.isError ? mutation.error.message : undefined}
			onConfirm={() => {
				if (apiKey !== undefined) {
					void revoke(apiKey);
				}
			}}
			onClose={close}
		>
			Software using this key is refused from its next request on. A revoked key never works again: to let the
			software back in, create a new one.
		</ConfirmDialog>
	);
}
